import { CalendarIcons } from 'owa-calendar-icons';
import { ControlIcons } from 'owa-control-icons';
import { FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc, { format } from 'owa-localize';
import { isEduStudent } from 'owa-bpos-utils';
import type { SaveCalendarViewConfig } from 'owa-save-calendar-view-data';
import {
    switchToDayText,
    switchToMultiDayText,
    switchToWorkWeekText,
    switchToSchoolWeekText,
    switchToWeekText,
    switchToMonthText,
    switchToBoardText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';
import { lazyGetSelectedBoardProjectName } from 'owa-calendar-board-commanding';
import { IButtonStyles } from '@fluentui/react/lib/Button';

import styles from '../../components/CalendarModuleCommandBar.scss';

/**
 * Unique identifier allowing other components to search for this item
 * E.g., via getTargetById('BOARD_VIEW_VIEW_SWITCHER')
 *
 * This string is not imported/exported so consumers search for this
 * item by duplicating this string.
 * If this is renamed or removed please update all references.
 */
const BoardViewViewSwitcherId = 'BOARD_VIEW_VIEW_SWITCHER';

export interface ViewTypeDefinition {
    [FilterableMenuItemArgs.name]: string;
    [FilterableMenuItemArgs.icon]: ControlIcons | CalendarIcons | null;
    [FilterableMenuItemArgs.title]: string;
    [FilterableMenuItemArgs.id]?: string;
    [FilterableMenuItemArgs.buttonStyles]?: IButtonStyles;
}

export function getSavedViewTypeDefinition(viewConfig: SaveCalendarViewConfig): ViewTypeDefinition {
    const getDisplayableSavedViewName = (viewName: string): string => {
        if (viewName?.length > 20) {
            return viewName.substring(0, 20) + '...';
        }
        return viewName;
    };

    return {
        [FilterableMenuItemArgs.name]: getDisplayableSavedViewName(viewConfig.viewName),
        [FilterableMenuItemArgs.icon]: null,
        [FilterableMenuItemArgs.title]: viewConfig.viewName,
    };
}

export function getDayViewTypeDefinition(): ViewTypeDefinition {
    const label = loc(switchToDayText);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarDay,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getMultiDayViewTypeDefinition(numOfDays: number): ViewTypeDefinition {
    const label = format(loc(switchToMultiDayText), numOfDays);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarDay,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getWorkWeekViewTypeDefinition(): ViewTypeDefinition {
    const label = isEduStudent() ? loc(switchToSchoolWeekText) : loc(switchToWorkWeekText);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarWorkWeek,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getWeekViewTypeDefinition(): ViewTypeDefinition {
    const label = loc(switchToWeekText);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarWeek,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getMonthViewTypeDefinition(): ViewTypeDefinition {
    const label = loc(switchToMonthText);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: ControlIcons.Calendar,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getBoardViewTypeDefinition(): ViewTypeDefinition {
    const label = loc(switchToBoardText);
    return {
        [FilterableMenuItemArgs.name]: label,
        [FilterableMenuItemArgs.icon]: ControlIcons.Taskboard,
        [FilterableMenuItemArgs.title]: label,
    };
}

export function getBoardNameViewTypeDefinition(): ViewTypeDefinition {
    const getSelectedBoardProjectName = lazyGetSelectedBoardProjectName.tryImportForRender();
    const projectName = getSelectedBoardProjectName
        ? getSelectedBoardProjectName()
        : loc(switchToBoardText);
    return {
        [FilterableMenuItemArgs.id]: BoardViewViewSwitcherId,
        [FilterableMenuItemArgs.name]: projectName,
        [FilterableMenuItemArgs.icon]: ControlIcons.Taskboard,
        [FilterableMenuItemArgs.title]: projectName,
        [FilterableMenuItemArgs.buttonStyles]: {
            label: styles.viewSwitcherLimitWidth,
        },
    };
}
